import { useEffect, useState } from 'react'
import { Location, useMapType } from '@components/Location/location.types'
import { useLoadScript } from '@react-google-maps/api'
import useDidMountEffect from '@utilities/hooks/useDidMountEffect'
import useUserLocation from '@components/Location/reusable/useUserLocation'
import LatLngBounds = google.maps.LatLngBounds

const MIN_ZOOM_LEVEL = 4

const useMap: useMapType = (props, settings = { filterLocations: true }) => {
  const { allLocations, locationsInArea, zipCodePosition, zipCode } = props
  const [isVisible, setIsVisible] = useState<boolean>(true)
  const [activeMarker, setActiveMarker] = useState<number | null>(null)
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY as string,
  })
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
  const [locationsOnMap, setLocationsOnMap] = useState<Location[]>([])
  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false)
  const [initialBoundaries, setInitialBoundaries] = useState<
    undefined | LatLngBounds
  >()

  const [userPosition] = useUserLocation()

  const handleOnLoad = (map: google.maps.Map) => {
    setNeedsRefresh(!isVisible)
    if (!mapInstance) {
      setMapInstance(map)
    }

    map.setOptions({ minZoom: MIN_ZOOM_LEVEL })

    const numberOfLocationsInArea = locationsInArea ? locationsInArea.length : 0

    if (zipCode && zipCodePosition) {
      if (!isVisible) {
        return setLocationsOnMap(locationsInArea ? locationsInArea : [])
      }

      if (numberOfLocationsInArea === 0) {
        const MAX_ZOOM_LEVEL = 13
        map.setZoom(MAX_ZOOM_LEVEL)

        map?.setCenter({
          lat: Number(zipCodePosition.lat),
          lng: Number(zipCodePosition.lng),
        })
      } else if (numberOfLocationsInArea === 1 && locationsInArea) {
        const { lat, lng } = locationsInArea[0]
        const MAX_ZOOM_LEVEL = 16
        map.setZoom(MAX_ZOOM_LEVEL)

        map?.setCenter({
          lat: Number(lat),
          lng: Number(lng),
        })
      } else if (numberOfLocationsInArea > 1 && locationsInArea) {
        const bounds = new google.maps.LatLngBounds()
        locationsInArea.forEach(({ lat, lng }) =>
          bounds.extend({ lat: Number(lat), lng: Number(lng) })
        )
        map.fitBounds(bounds)
      }

      setInitialBoundaries(map.getBounds())
    } else {
      setInitialBoundaries(undefined)

      if (!isVisible) return setLocationsOnMap(allLocations)

      if (userPosition) {
        const MAX_ZOOM_LEVEL = 12
        map.setZoom(MAX_ZOOM_LEVEL)

        map?.setCenter({
          lat: userPosition?.coords.latitude,
          lng: userPosition?.coords.longitude,
        })
      } else {
        const bounds = new google.maps.LatLngBounds()
        allLocations.forEach(({ lat, lng }) =>
          bounds.extend({ lat: Number(lat), lng: Number(lng) })
        )
        map.fitBounds(bounds)
      }
    }
  }

  const handleReloading = (map: google.maps.Map) => {
    if (!isVisible) return
    if (needsRefresh) {
      handleOnLoad(map)
    } else {
      const bounds = map.getBounds()
      const zoom = map.getZoom()

      if (bounds && zoom) {
        map.fitBounds(bounds)
        map.setZoom(zoom)
      }
    }
  }

  const filterVisibleLocations = () => {
    if (mapInstance && settings.filterLocations) {
      const mapBounds = mapInstance.getBounds()
      if (mapBounds) {
        const visibleLocations = allLocations.filter((location) => {
          if (location.lat && location.lng) {
            const loc = new google.maps.LatLng(
              Number(location.lat),
              Number(location.lng)
            )
            return mapBounds.contains(loc)
          }
          return false
        })

        setLocationsOnMap(visibleLocations)
      }
    }
  }

  useDidMountEffect(() => {
    if (!isVisible) {
      setIsVisible(true)
    }
    if (mapInstance) {
      handleOnLoad(mapInstance)
    }
  }, [locationsInArea, userPosition])

  useDidMountEffect(() => {
    if (mapInstance) {
      handleReloading(mapInstance)
    }
  }, [isVisible])

  useEffect(() => {
    if (mapInstance) {
      const boundsChangedListener = google.maps.event.addListener(
        mapInstance,
        'bounds_changed',
        () => {
          filterVisibleLocations()
        }
      )

      return () => {
        google.maps.event.removeListener(boundsChangedListener)
      }
    }
  }, [isLoaded, mapInstance])

  const handleActiveMarker = (markerId: number | null) => {
    if (markerId === activeMarker) {
      return
    }
    setActiveMarker(markerId)
  }

  const changePosition = (lat: number, lng: number) => {
    mapInstance?.setCenter({ lat, lng })
    mapInstance?.setZoom(15)
  }

  const showMap = () => {
    setIsVisible(true)
    if (mapInstance && mapInstance.getBounds()) {
      const bounds = mapInstance.getBounds()
    }
  }

  const hideMap = () => {
    setIsVisible(false)
  }

  return {
    state: {
      mapInstance,
      locationsOnMap,
      isLoaded,
      activeMarker,
      isVisible,
      initialBoundaries,
    },
    handlers: {
      handleActiveMarker,
      changePosition,
      handleOnLoad,
      showMap,
      hideMap,
    },
  }
}

export default useMap
